import React from "react";
import SocialIcon from "@/app/components/pages/SocialIcons";
import Image from "next/image";
import {
  COMPANY_DISCORD_INVITE_LINK,
  COMPANY_TWITTER_LINK,
} from "@/constants/sleapContact";
import { useTranslations } from "next-intl";
import dayjs from "@dayjs";

const footerLinks = (t: ReturnType<typeof useTranslations>) => {
  return [
    {
      title: t("Footer.company"),
      links: [
        { name: t("Footer.termsAndConditions"), url: "/conditions" },
        { name: t("Footer.aboutPage"), url: "/about" },
      ],
    },
    {
      title: t("Footer.support"),
      links: [
        { name: t("Footer.contact"), url: "/contact" },
        { name: t("Footer.faq"), url: "/faq" },
      ],
    },
    {
      title: t("Footer.legal"),
      links: [
        { name: t("Footer.imprint"), url: "/imprint" },
        { name: t("Footer.privacyPolicy"), url: "/privacy-policy" },
      ],
    },
  ];
};

function Footer() {
  const t = useTranslations();

  return (
    <div className="bg-lightwhite dark:bg-sleap-gray-950">
      <div className="overflow-hidden">
        <div className="opacity-30 relative flex justify-center blur-3xl">
          <div className="absolute w-[1200px] h-[300px] z-[1] sm:top-[80vh] top-[30vh] bg-sleap-purple-200 rounded-[600px/150px] transform-[rotate(10deg)] bgmd:top-[250px] bgmd:left-[400px] bglg:top-[200px] bglg:left-[-100px]"></div>
          <div className="absolute w-[1400px] h-[250px] z-[0] sm:top-[60vh] top-[45vh] bg-sleap-blue-200 rounded-[700px/125px] transform-[rotate(8deg)] bgmd:top-[250px] bgmd:right-[300px] bglg:top-[165px] bglg:right-[-200px] sm:pb-0 pb-4"></div>
        </div>
        <footer className="container relative z-1 !py-8 md:!py-16 w-full mx-auto px-4">
          <div className="mb-6 md:mb-12 flex justify-start">
            <div>
              <Image
                src="/assets/v2/sleap-staynex-logo-white.svg"
                className="sm:h-16 h-14 sm:w-auto sm:max-w-[192px] dark:block hidden"
                width={192}
                height={60}
                alt="Sleap Logo"
              />
              <Image
                src="/assets/v2/sleap-staynex-logo-dark.svg"
                className="sm:h-16 h-14 sm:w-auto sm:max-w-[192px] dark:hidden block"
                width={192}
                height={60}
                alt="Sleap Logo"
              />
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-y-6 lg:gap-x-24 gap-x-4 md:justify-between">
            <div className="w-full md:w-1/2">
              <p className="text-normal md:text-base text-sm text-left dark:text-sleap-gray-100 text-sleap-gray-800 mb-0">
                {" "}
                {t("Footer.about")}
              </p>
            </div>
            <div className="flex md:flex-row flex-col dark:text-white text-sleap-gray-950 gap-6 sm:gap-4 justify-start lg:justify-center">
              {footerLinks(t).map((section) => (
                <div className="lg:w-[164px]" key={section.title}>
                  <div className="mb-3 font-medium text-lg font-unbounded">
                    {section.title}
                  </div>
                  <div className="flex sm:flex-col flex-row gap-2 text-base font-normal font-inter">
                    {section.links.map((link) => (
                      <a
                        key={link.name}
                        className="primary"
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {link.name}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-2 justify-start mt-4 mb-6 sm:mb-0">
            <SocialIcon
              url={`${COMPANY_DISCORD_INVITE_LINK}`}
              target="_blank"
              icon={"/assets/v2/icons/brand-discord-solid.svg"}
              lightIcon={"/assets/v2/icons/light-brand-discord.svg"}
              twClasses="h-6 w-6"
              alt="Sleap Discord Channel"
              text={t("Footer.discord")}
            />
            <SocialIcon
              url={`${COMPANY_TWITTER_LINK}`}
              target="_blank"
              icon={"/assets/v2/icons/brand-x-solid.svg"}
              lightIcon="/assets/v2/icons/light-brand-x.svg"
              twClasses="h-6 w-6"
              alt="Sleap Twitter Profile"
              text={t("Footer.twitter")}
            />
          </div>
        </footer>
        <div className="relative bg-white dark:bg-sleap-gray-950  dark:text-white text-sleap-gray-950  py-4 text-center">
          ©{dayjs().year()} {t("Footer.allRightReserved")}
        </div>
      </div>
    </div>
  );
}

export default Footer;
